/**
 * Light/dark theme, client side.
 *
 *   - `<html data-theme>` is rendered from the `smtpx_theme` cookie, and <ThemeScript> sets the same value before first paint.
 *   - Signed-out visitors: the choice lives in localStorage only.
 *   - Signed-in users: the choice is also saved to the database (POST /api/theme) and, on a
 *     new device, pulled from there (GET /api/theme) by <ThemeSync>.
 *   - A non-HttpOnly cookie `smtpx_in=1` (set at sign-in) tells us whether to bother the API.
 */
export type Theme = "dark" | "light";

export const THEME_KEY = "smtpx-theme";
/** Sent on every request so the server can paint the same theme the browser stored. */
export const THEME_COOKIE = "smtpx_theme";
export const THEME_COLORS: Record<Theme, string> = { dark: "#0c0e11", light: "#f2f4f7" };

export const isTheme = (v: unknown): v is Theme => v === "dark" || v === "light";

export function currentTheme(): Theme {
  return document.documentElement.dataset.theme === "light" ? "light" : "dark";
}

export function isSignedIn(): boolean {
  return /(?:^|;\s*)smtpx_in=1/.test(document.cookie);
}

/** Apply a theme to the page. `persist` also writes localStorage. */
export function applyTheme(theme: Theme, opts: { persist?: boolean; animate?: boolean } = {}) {
  const { persist = true, animate = true } = opts;
  const root = document.documentElement;
  if (animate) {
    root.classList.add("theme-fade");
    window.setTimeout(() => root.classList.remove("theme-fade"), 450);
  }
  root.dataset.theme = theme;
  if (persist) {
    try {
      localStorage.setItem(THEME_KEY, theme);
    } catch {
      /* private mode: the choice just won't survive a reload */
    }
    document.cookie = `${THEME_COOKIE}=${theme}; Path=/; Max-Age=31536000; SameSite=Lax`;
  }
  document.querySelector('meta[name="theme-color"]')?.setAttribute("content", THEME_COLORS[theme]);
  window.dispatchEvent(new CustomEvent("themechange", { detail: theme }));
}

/** Persist to the database when signed in. Never throws; localStorage stays the source of truth offline. */
export async function saveThemeRemote(theme: Theme): Promise<void> {
  if (!isSignedIn()) return;
  try {
    await fetch("/api/theme", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ theme }),
      credentials: "same-origin",
    });
  } catch {
    /* ignore */
  }
}

export function setTheme(theme: Theme) {
  applyTheme(theme);
  void saveThemeRemote(theme);
}

export function toggleTheme(): Theme {
  const next: Theme = currentTheme() === "dark" ? "light" : "dark";
  setTheme(next);
  return next;
}
